import { NgbModalRef, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, skip, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { DsButtonVariants, DsModalService, DsModalVariant, Icons, IconStyles } from '@levelaccess/design-system';
import { map as lodashMap } from 'lodash';

import { A11yService } from '../../../../../../services/a11y.service';
import { BusMessageChannels, BusMessageService } from '../../../../../../services/bus-message.service';
import { FeatureFlagService } from '../../../../../../services/feature-flag/feature-flag.service';
import { Api, ApiQueryOption } from '../../../../../../../../shared/constants/api';
import { IUploadClient, IUploadWithThumbnailClient } from '../../../../../../../../shared/interfaces/uploads.interface';
import { ManualAuditIssueService } from '../../../../../../services/manual-audit-issue.service';
import { TranslateService } from '../../../../../../translate/translate.service';
import { RestBuilder } from '../../../../../../services/helpers/rest.builder';
import {
  $auditFinding,
  $auditFindingViewport,
  ActiveAuditFindingQAStatus,
  AuditFindingQAStatus,
  AuditFindingQAStatusDisplayLabel,
  AuditFindingReferenceType,
  IgnoredStatusAllowedSources,
  MAX_ATTACHMENTS,
} from '../../../../../../../../shared/constants/audit-finding';
import {
  IManualAuditIssueServerResponse,
  IManualEvaluation,
  IPopulatedManualAuditIssueServerResponse,
} from '../../../../../../../../shared/interfaces/manual-audit.interface';
import { IManualAuditRuleReport } from '../../../../../../../../shared/interfaces/audit-reports/audit-report.interface';
import { $auditRule, ManualCustomRulePrefix } from '../../../../../../../../shared/constants/audit-rule';
import { $auditIssueFields } from '../../../../../../../../shared/constants/manual-audit-issues';
import { $uploads } from '../../../../../../../../shared/constants/uploads';
import { $auditStandardSuccessCriteria } from '../../../../../../../../shared/constants/audit-standard-success-criteria';
import { MimeTypes, SupportedManualAuditAttachmentMimeType } from '../../../../../../../../shared/constants/mime-type';
import { ICurrentSelectedProperty, UserService } from '../../../../../../services/user.service';
import { UserAclService } from '../../../../../../services/user-acl.service';
import { $formFields } from '../constants/form-fields';
import { ManualAuditIssueCommentSectionComponent } from '../comment-section/manual-audit-issue-comment-section.component';
import { UserPropertyService } from '../../../../../../services/user-property.service';
import { LoadErrorHandler } from '../../../../../../services/load-error-handler';
import { NotificationService } from '../../../../../../services/notification.service';
import { NotificationPosition } from '../../../../../../models/notification.model';
import { DateUtility } from '../../../../../../utility/date.utility';
import { CommonUtility } from '../../../../../../utility/common.utility';
import { RecordTabTitleUtility } from '../../../../../../utility/record-tab-title.utility';
import { IHTMLInputEvent } from '../../../../../../interfaces/form.interface';
import { config } from '../../../../../../../environments/config.shared';
import { ModalService } from '../../../../../../services/modal.service';
import { ITask } from '../../../../../../../../shared/interfaces/task.interface';
import { TenantService } from '../../../../../../services/tenant.service';
import { InstrumentationService } from '../../../../../../services/instrumentation.service';
import { pageLoadingState } from '../../../../../../components/page-loading-state/page-loading-state.component';
import {
  $ruleAuditHistory,
  $ruleAuditHistoryExtra,
  RuleAuditHistoryAction,
} from '../../../../../../../../shared/constants/rule-audit-history';
import { IssueActionModalComponent } from '../../../../scans/scan-results/reports/issue-action-modal/issue-action-modal.component';
import { SharedCommonUtility } from '../../../../../../../../shared/utils/common.utility';
import { $falsePositiveError } from '../../../../../../../../shared/constants/false-positive-error';
import { UrlFactory } from '../../../../../../components/share-issue-report-url/share-issue-report-url.component';
import {
  IManualAuditHistoryRequestFilter,
  IPopulatedManualFindingClient,
  IPopulatedManualFindingSuccessCriteria,
} from '../../../../../../../../shared/interfaces/manual-audit-finding.interface';
import { $severity } from '../../../../../../../../shared/constants/accessibility';
import { FileUploadService } from '../../../../../../services/file-upload.service';
import { WorkspaceService } from '../../../../../../services/workspace.service';
import { IWorkspace } from '../../../../../../../../shared/interfaces/workspace.interface';
import { ShortLinkService } from '../../../../../../services/short-link.service';
import { $workspace } from '../../../../../../../../shared/constants/workspace';
import { $manualAudit, ManualEvaluationSource } from '../../../../../../../../shared/constants/manual-audit';
import { IManualAuditHistoryRequest } from '../../../../../../interfaces/manual-evaluation.interface';
import { IRequiredSecurity } from '../../../../../../../../shared/interfaces/security.interface';
import { RequiredSecurities } from '../../../../../../../../shared/constants/required-securities';
import { AclSecurityAdapter } from '../../../../../../services/acl.service';
import { FunctionalArea, SecureAction } from '../../../../../../../../shared/constants/security';
import { IEditAttachment } from '../../../../../../components/attachment-thumbnail/edit-attachment/edit-attachment-modal.component';
import { $task, TaskSource } from '../../../../../../../../shared/constants/task';
import { ILinkedPropertyData } from '../../../../../../../../shared/utils/linked-property.utility';
import { SuccessCriteriaService } from '../../../../../../services/success-criteria.service';
import { ClipboardUtilityService } from '../../../../../../services/clipboard-utility.service';
import { DeleteConfirmDialogComponent } from './delete-confirm-dialog.component';
import { UpsertTaskSlideoutComponent } from '../../../../projects/tasks/upsert-task-slideout/upsert-task-slideout.component';
import { EditManualAuditIssueComponent } from './edit/edit-manual-audit-issue.component';
import { Origin } from '../../../../../../../../shared/interfaces/scan.interface';
import { AngularUtility } from '../../../../../../utility/angular.utility';
import { LINKED_RECORD_TAB_INDEX } from '../../../../../../constants/linked-comments.constants';
import { TenantSeveritiesService } from '../../../../../../services/tenant-severities.service';
import { ICustomSeveritiesMap, ITenantInfoWithPackageInfo } from '../../../../../../../../shared/interfaces/tenant.interface';
import {
  EVALUATION_STANDARD_TO_AUDIT_STANDARD,
  EvaluationStandards,
} from '../../../../../../../../shared/constants/manual-evaluation';
import { SharedAuditsUtility } from '../../../../../../../../shared/utils/audits.utility';
import {
  ChangeFindingFlagModalComponent,
  IFlagCommentFormValue,
} from './change-finding-flag-modal/change-finding-flag-modal.component';
import { CommentService } from '../../../../../../services/comment.service';
import { IComment } from '../../../../../../../../shared/interfaces/comment.interface';
import { $manualAuditIssueComment, ManualFindingFlagReason } from '../../../../../../../../shared/constants/comment';
import { FeatureFlagCollection } from '../../../../../../../../shared/interfaces/feature-flag.interface';

export interface ISelectOption<T> {
  displayName: string;
  value: T;
}

interface IRouteParams {
  evaluationScanId: string;
  manualAuditIssueId: string;
}

@Component({
  selector: 'app-view-manual-audit-issue',
  templateUrl: './view-manual-audit-issue.component.html',
  styleUrls: ['./view-manual-audit-issue.component.scss'],
  standalone: false,
})
export class ViewManualAuditIssueComponent implements OnInit, OnDestroy {
  @ViewChild(ManualAuditIssueCommentSectionComponent)
  private commentSectionComponent: ManualAuditIssueCommentSectionComponent;
  private restBuilder: RestBuilder;
  private subscriptions: Subscription;
  private getIssueSubscription: Subscription;
  private announcedRecordTabId: string = '';
  private routeParams$: Observable<IRouteParams>;
  private updateAttachment$: Subject<IEditAttachment> = new Subject<IEditAttachment>();
  private removeAttachment$: Subject<string> = new Subject<string>();
  private uploadAttachment$: Subject<FormData> = new Subject<FormData>();
  private lastUploadFilesExcluded: boolean = false;

  protected SharedCommonUtility: typeof SharedCommonUtility = SharedCommonUtility;
  protected Origin: typeof Origin = Origin;

  public formValidationRequest$: Observable<void>;
  public hasCreateAttachmentPermissions: boolean;
  public hasDeleteAttachmentPermissions: boolean;
  public manualAuditIssueId: string;
  public issue: IPopulatedManualFindingClient;
  public $auditFinding: typeof $auditFinding;
  public $auditFindingViewport: typeof $auditFindingViewport;
  public AuditFindingQAStatusDisplayLabel: typeof AuditFindingQAStatusDisplayLabel;
  public issueUrl: string;
  public auditName: string;
  public ruleReport: IManualAuditRuleReport;
  public digitalPropertyId: string;
  public workspaceId: string;
  public digitalPropertyKey: string;
  public digitalPropertyName: string;
  public embedScreenshot: boolean;
  public issueStatus: ISelectOption<AuditFindingQAStatus>;
  public SecurityContext: typeof SecurityContext;
  public AuditFindingQAStatus: typeof AuditFindingQAStatus;
  public $severity: typeof $severity;
  public allRegularRestoreStatuses$: Observable<ISelectOption<AuditFindingQAStatus>[]>;
  public showIgnoredStatus$: Observable<boolean>;
  public showToReviewStatus$: Observable<boolean>;
  public dismissedStatus: ISelectOption<AuditFindingQAStatus>;
  public openStatus: ISelectOption<AuditFindingQAStatus>;
  public hasStatusEditPermissions: boolean;
  public hasDismissalPermissions: boolean;
  public $auditIssueFields: typeof $auditIssueFields = $auditIssueFields;
  public mimeType: typeof MimeTypes;
  public attachmentsUploader: any[];
  public isDragAreaActive: boolean;
  public uploaderError: string;
  public linkedTask: ITask;
  public isCreateTaskSuccess: boolean;
  public $task: typeof $task = $task;
  public linkedCommentsFlag$: Observable<boolean>;
  public canReadLinkedTaskComments$: Observable<boolean>;
  public pageLoadingState: typeof pageLoadingState;
  public loadingState: pageLoadingState;
  public issueShortLink: UrlFactory;
  public evaluationScanId: string;
  public manualAuditHistoryRequest: IManualAuditHistoryRequest;
  public hasHistory$: Observable<boolean>;
  public tasksCreateRequiredSecurity: IRequiredSecurity;
  public customSeveritiesData: ICustomSeveritiesMap;
  public customSeverityKeysSortedByRank: $severity[];

  public selectedStatus: AuditFindingQAStatus;
  public readonly AuditFindingReferenceType: typeof AuditFindingReferenceType = AuditFindingReferenceType;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;
  public readonly IconStyles: typeof IconStyles = IconStyles;
  public showTaskStatusChangedBanner: boolean;
  public RequiredSecurities: typeof RequiredSecurities = RequiredSecurities;
  public $uploads: typeof $uploads = $uploads;
  protected userOrigin$: Observable<Origin>;
  protected evaluationOrigin$: Observable<Origin>;
  protected evaluationSource$: Observable<ManualEvaluationSource>;
  protected manualEvaluation$: Observable<IManualEvaluation>;

  constructor(
    private router: Router,
    private busMessageService: BusMessageService,
    private activatedRoute: ActivatedRoute,
    private manualAuditIssueService: ManualAuditIssueService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private changeDetector: ChangeDetectorRef,
    private userPropertyService: UserPropertyService,
    private userAclService: UserAclService,
    private modalService: ModalService,
    private loadErrorHandler: LoadErrorHandler,
    private fileUploadService: FileUploadService,
    private workspaceService: WorkspaceService,
    private shortLinkService: ShortLinkService,
    private successCriteriaService: SuccessCriteriaService,
    private clipboardUtilityService: ClipboardUtilityService,
    private dsModalService: DsModalService,
    private userService: UserService,
    private tenantSeveritiesService: TenantSeveritiesService,
    private commentService: CommentService,
    private featureFlagService: FeatureFlagService,
    private tenantService: TenantService,
    private instrumentationService: InstrumentationService,
    private elementRef: ElementRef<HTMLElement>,
    private titleService: Title,
    private a11yService: A11yService,
  ) {
    this.restBuilder = new RestBuilder();
    this.subscriptions = new Subscription();
    this.hasCreateAttachmentPermissions = null;
    this.hasDeleteAttachmentPermissions = null;
    this.issue = null;
    this.AuditFindingQAStatus = AuditFindingQAStatus;
    this.$auditFinding = $auditFinding;
    this.$auditFindingViewport = $auditFindingViewport;
    this.$severity = $severity;
    this.AuditFindingQAStatusDisplayLabel = AuditFindingQAStatusDisplayLabel;
    this.ruleReport = null;
    this.digitalPropertyId = null;
    this.workspaceId = null;
    this.digitalPropertyKey = '';
    this.digitalPropertyName = '';
    this.issueStatus = null;
    this.embedScreenshot = false;
    this.manualAuditIssueId = this.activatedRoute.snapshot.params[Api.manualAuditIssueId];
    this.evaluationScanId = this.activatedRoute.snapshot.params[Api.scan_id];
    this.SecurityContext = SecurityContext;
    this.hasStatusEditPermissions = false;
    this.hasDismissalPermissions = false;
    this.dismissedStatus = {
      value: AuditFindingQAStatus.dismissed,
      displayName: this.translateService.instant(AuditFindingQAStatusDisplayLabel[AuditFindingQAStatus.dismissed]),
    };
    this.openStatus = {
      value: AuditFindingQAStatus.open,
      displayName: this.translateService.instant(AuditFindingQAStatusDisplayLabel[AuditFindingQAStatus.open]),
    };
    this.mimeType = MimeTypes;
    this.attachmentsUploader = [];
    this.isDragAreaActive = false;
    this.uploaderError = null;
    this.linkedTask = null;
    this.isCreateTaskSuccess = false;
    this.linkedCommentsFlag$ = this.tenantService.getTenantFeatureFlag(FeatureFlagCollection.findingsTaskLinkedComments);
    this.canReadLinkedTaskComments$ = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter.useWorkspaceFromUser().useFunctionalActions(RequiredSecurities.PM_TaskComments_Read.functionalActions).check(),
        ),
      );
    this.pageLoadingState = pageLoadingState;
    this.loadingState = pageLoadingState.loading;
    this.tasksCreateRequiredSecurity = RequiredSecurities.PM_Tasks_Create;
    this.showTaskStatusChangedBanner = false;

    if (typeof this.manualAuditIssueId === 'undefined') {
      console.warn('[ViewManualAuditIssueComponent.getIssue] manual audit issue id for issue is not available', {
        evaluationScanId: this.evaluationScanId,
        manualAuditIssueId: this.manualAuditIssueId,
      });
      return;
    }
  }

  private getRouteParams(): Observable<IRouteParams> {
    return this.activatedRoute.params.pipe(
      map((params: Params) => {
        const manualAuditIssueId = params[Api.manualAuditIssueId];
        const evaluationScanId = params[Api.scan_id];

        if (typeof manualAuditIssueId === 'undefined') {
          console.warn('[ViewManualAuditIssueComponent.getIssue] manual audit issue id for issue is not available', {
            manualAuditIssueId,
            evaluationScanId,
          });
          return undefined;
        }

        return {
          evaluationScanId,
          manualAuditIssueId,
        };
      }),
    );
  }

  private updateCustomRuleDescription(): void {
    if (this.ruleReport === null) {
      return;
    }

    if (typeof this.ruleReport.ruleId === 'string' && this.ruleReport.ruleId.startsWith(ManualCustomRulePrefix)) {
      this.ruleReport.ruleDescription = this.translateService.instant('issue-custom-rule-title', this.digitalPropertyName);
    }
  }

  private onGetIssueSuccess(response: IPopulatedManualAuditIssueServerResponse): void {
    this.issue = response.issue;
    this.issueUrl = response.issueUrl;
    this.auditName = response.manualAuditTitle;
    this.linkedTask = response.linkedTask;
    this.issueStatus = {
      value: this.issue[$auditFinding.QAStatus] as AuditFindingQAStatus,
      displayName: this.translateService.instant(AuditFindingQAStatusDisplayLabel[this.issue[$auditFinding.QAStatus]]),
    };
    this.selectedStatus = this.issueStatus.value;

    const successCriterias: IPopulatedManualFindingSuccessCriteria[] = SharedAuditsUtility.getScopedSuccessCriteria(
      this.issue[$auditFinding.ruleId][$auditRule.successCriterias],
      response.manualAuditStandards,
    );

    const findingStandards: EvaluationStandards[] = SharedAuditsUtility.getEvaluationStandardsOfSuccessCriteria(
      response.manualAuditStandards,
      successCriterias,
    );

    this.embedScreenshot = this.manualAuditIssueService.canInlineScreenshot(this.issue[$auditFinding.screenshot]?.[0]);

    this.ruleReport = {
      ruleDescription: this.issue[$auditFinding.ruleId][$auditRule.description],
      ruleId: this.issue[$auditFinding.ruleId][$auditRule.ruleId],
      severity: this.issue[$auditFinding.ruleId][$auditRule.severity] as $severity,
      successCriterias: this.successCriteriaService.getSuccessCriteriaListFromStandards(
        lodashMap(successCriterias, $auditStandardSuccessCriteria.identifier),
        findingStandards.map((standard: EvaluationStandards) => EVALUATION_STANDARD_TO_AUDIT_STANDARD[standard]),
      ),
      standards: findingStandards,
      disabilitiesAffected: this.issue[$auditFinding.disabilityAffected],
      compliantCodeExample: this.issue[$auditFinding.ruleId][$auditRule.compliantCodeExample],
      nonCompliantCodeExample: this.issue[$auditFinding.ruleId][$auditRule.nonCompliantCodeExample],
      techniques: this.issue[$auditFinding.ruleId][$auditRule.techniques],
    };

    this.updateCustomRuleDescription();
    this.issueShortLink = async (): Promise<string> => {
      return this.manualAuditIssueService.getShortLink(this.evaluationScanId, this.manualAuditIssueId);
    };

    this.manualAuditHistoryRequest = {
      scanId: this.evaluationScanId,
      issueId: this.manualAuditIssueId,
      filter: this.dismissedAndRestoredRuleAuditHistoryFilters(),
    };

    this.hasHistory$ = this.manualAuditIssueService.hasHistory(this.manualAuditHistoryRequest);

    this.loadingState = pageLoadingState.loaded;
    this.applyManualFindingTabTitle();

    this.changeDetector.detectChanges();
  }

  private applyManualFindingTabTitle(): void {
    const issueId: string = this.issue?.[$auditFinding.issueId];
    if (SharedCommonUtility.isNullishOrEmpty(this.digitalPropertyKey) || SharedCommonUtility.isNullishOrEmpty(issueId)) {
      return;
    }

    const customerFacingId: string = `${this.digitalPropertyKey}\u2013${issueId}`;
    const pageLabel: string = RecordTabTitleUtility.pageLabel(this.translateService, 'findings_details', 'Findings details');
    const idAndLabel: string = `${customerFacingId} - ${pageLabel}`;
    this.titleService.setTitle(`${idAndLabel} | ${config.APP.NAME}`);

    if (this.announcedRecordTabId === customerFacingId) {
      return;
    }

    this.announcedRecordTabId = customerFacingId;
    this.a11yService.setMessage(RecordTabTitleUtility.announcement(this.translateService, idAndLabel));
  }

  private dismissedAndRestoredRuleAuditHistoryFilters(): IManualAuditHistoryRequestFilter[] {
    return [
      {
        [$ruleAuditHistory.action]: RuleAuditHistoryAction.statusUpdate,
        [$ruleAuditHistory.extra]: {
          [$ruleAuditHistoryExtra.to]: AuditFindingQAStatus.dismissed,
        },
      },
      {
        [$ruleAuditHistory.action]: RuleAuditHistoryAction.statusUpdate,
        [$ruleAuditHistory.extra]: {
          [$ruleAuditHistoryExtra.from]: AuditFindingQAStatus.dismissed,
          [$ruleAuditHistoryExtra.to]: AuditFindingQAStatus.open,
        },
      },
    ];
  }

  private onGetIssueError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('view_finding_error', response, ViewManualAuditIssueComponent.name);
    this.loadingState = pageLoadingState.error;
  }

  private onUpdateIssueError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('update_finding_details_error', response, ViewManualAuditIssueComponent.name);
  }

  private onRemoveAttachmentSuccess(response: string[]): void {
    this.notificationService.success(
      this.translateService.instant('success_removing_manual_issue_attachment'),
      NotificationPosition.Toast,
    );

    const scroller: HTMLElement = this.findScrollContainer();
    const previousScrollTop: number = scroller.scrollTop;

    this.issue[$auditFinding.attachment] = this.issue[$auditFinding.attachment].filter(
      (attachment: IUploadClient): boolean => response.includes(attachment[$uploads._id] as string) === false,
    );

    this.changeDetector.detectChanges();
    this.restorePageScrollTop(scroller, previousScrollTop);
  }

  /**
   * Finds the nearest scrollable ancestor of this component. In the vertical navigation shell the page
   * scrolls inside `.main-content-container` rather than at the document level, so we cannot assume
   * `document.scrollingElement`. Falls back to the document scrolling element (legacy layout).
   */
  private findScrollContainer(): HTMLElement {
    let node: HTMLElement | null = this.elementRef.nativeElement;

    while (node !== null && node !== document.body && node !== document.documentElement) {
      const overflowY: string = getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }

    return (document.scrollingElement as HTMLElement) ?? document.documentElement;
  }

  private restorePageScrollTop(scroller: HTMLElement, scrollTop: number): void {
    const applyScrollTop = (): void => {
      if (scroller.scrollTop === scrollTop) {
        return;
      }
      const previousScrollBehavior: string = scroller.style.scrollBehavior;
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollTop = scrollTop;
      scroller.style.scrollBehavior = previousScrollBehavior;
    };

    applyScrollTop();
    requestAnimationFrame(applyScrollTop);
  }

  private onRemoveAttachmentError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('remove_manual_audit_issue_attachment_error', response, ViewManualAuditIssueComponent.name);
  }

  private onUploadAttachmentSuccess(response: IUploadClient[]): void {
    if (this.lastUploadFilesExcluded === false) {
      const messageKey: string =
        response?.length > 1 ? 'success_uploading_manual_issue_attachments' : 'success_uploading_manual_issue_attachment';
      this.notificationService.success(this.translateService.instant(messageKey), NotificationPosition.Toast);
    }

    this.issue[$auditFinding.attachment] = [
      ...((Array.isArray(this.issue[$auditFinding.attachment]) ? this.issue[$auditFinding.attachment] : []) as IUploadClient[]),
      ...response,
    ];

    this.changeDetector.detectChanges();
  }

  private onUploadAttachmentError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('upload_manual_audit_issue_attachment_error', response, ViewManualAuditIssueComponent.name);
  }

  private buildAllRegularRestoreStatuses(): Observable<ISelectOption<AuditFindingQAStatus>[]> {
    const NON_REGULAR_RESTORE_STATUSES: Set<AuditFindingQAStatus> = new Set([
      AuditFindingQAStatus.toReview,
      AuditFindingQAStatus.dismissed,
      AuditFindingQAStatus.validationRequested,
    ]);

    const staticStatuses: ISelectOption<AuditFindingQAStatus>[] = ActiveAuditFindingQAStatus.filter(
      (status: AuditFindingQAStatus) => !NON_REGULAR_RESTORE_STATUSES.has(status),
    ).map((status: AuditFindingQAStatus): ISelectOption<AuditFindingQAStatus> => {
      return { value: status, displayName: this.translateService.instant(AuditFindingQAStatusDisplayLabel[status]) };
    });

    return combineLatest([this.showIgnoredStatus$, this.showToReviewStatus$]).pipe(
      map(([showIgnored, showToReview]: [boolean, boolean]) => {
        const resolvedStatuses = [...staticStatuses];

        if (showIgnored) {
          resolvedStatuses.push({
            value: AuditFindingQAStatus.ignored,
            displayName: this.translateService.instant(AuditFindingQAStatusDisplayLabel[AuditFindingQAStatus.ignored]),
          });
        }

        if (showToReview) {
          resolvedStatuses.unshift({
            value: AuditFindingQAStatus.toReview,
            displayName: this.translateService.instant(AuditFindingQAStatusDisplayLabel[AuditFindingQAStatus.toReview]),
          });
        }

        return resolvedStatuses;
      }),
    );
  }

  private buildFFEnabled(flag: FeatureFlagCollection): Observable<boolean> {
    return this.featureFlagService
      .variation$(flag, false)
      .pipe(map((value: boolean): boolean => (SharedCommonUtility.notNullish(value) ? Boolean(value) : value)));
  }

  private buildShowIgnoredStatus(): Observable<boolean> {
    return combineLatest([this.buildEditPermissionCheck(), this.userOrigin$, this.evaluationSource$]).pipe(
      map(([hasEdit, origin, source]: [boolean, Origin, ManualEvaluationSource]): boolean => {
        const isStaff = origin === Origin.staff;
        const hasAllowedSource = IgnoredStatusAllowedSources.includes(source);
        return hasEdit && isStaff && hasAllowedSource;
      }),
    );
  }

  private buildShowToReviewStatus(): Observable<boolean> {
    const ffEnabled$: Observable<boolean> = this.buildFFEnabled(FeatureFlagCollection.removeToReviewFromClientView);

    return combineLatest([ffEnabled$, this.userOrigin$, this.evaluationOrigin$]).pipe(
      map(([ffEnabled, userOrigin, evaluationOrigin]: [boolean, Origin, Origin]): boolean => {
        const hasAllowedOrigin = userOrigin === Origin.staff && evaluationOrigin === Origin.staff;
        return !ffEnabled || hasAllowedOrigin;
      }),
    );
  }

  private getCreateTaskDescription(baseUrl: string): string {
    return this.manualAuditIssueService.generateTaskDescription(baseUrl, this.issue, this.router.url);
  }

  public openCreateTaskSlideout(): void {
    this.subscriptions.add(
      this.workspaceService.getWorkspace(this.workspaceId).subscribe((workspace: IWorkspace) => {
        const baseUrl: string = this.shortLinkService.getBaseUrl(workspace[$workspace.tenant]);

        const component: UpsertTaskSlideoutComponent = this.dsModalService.open(
          UpsertTaskSlideoutComponent,
          DsModalVariant.slideOut,
        ).componentInstance;
        component.taskSource = TaskSource.manual;
        component.taskName = this.issue[$auditFinding.summary];
        component.description = this.getCreateTaskDescription(baseUrl);
        component.auditFindings = [this.issue[$auditFinding._id]];
        component.workspaceId = this.workspaceId;
        component.attachments = this.issue[$auditFinding.attachment] ?? [];
        component.notifyChanges = true;
        component.modalInit(true);

        this.subscriptions.add(
          component.onUpsert.subscribe(() => {
            this.getIssue();
            this.commentSectionComponent.ngOnInit();
            this.isCreateTaskSuccess = true;
          }),
        );
      }),
    );
  }

  public openEditIssueSlideout(event: MouseEvent): void {
    const editButton: HTMLElement = event.currentTarget as HTMLElement;
    const component: EditManualAuditIssueComponent = this.dsModalService.open(
      EditManualAuditIssueComponent,
      DsModalVariant.slideOut,
    ).componentInstance;
    component.evaluationScanId = this.evaluationScanId;
    component.manualAuditIssueId = this.manualAuditIssueId;
    component.modalInit();

    this.subscriptions.add(
      component.onSave.subscribe(() => {
        this.getIssue();
        this.commentSectionComponent.ngOnInit();
        // The success toast auto-focuses itself shortly after it appears (see ToastComponent.focusToast,
        // TOAST_FOCUS_DELAY = 100ms), which would otherwise steal focus back from the "Edit issue" button
        // that the slideout close already restored it to. Re-focus once that window has passed.
        window.setTimeout(() => editButton?.focus(), 150);
      }),
    );
  }

  private openRuleActionModal(
    actionType: RuleAuditHistoryAction,
    reasonIsRequired: boolean,
    action: (modal: IssueActionModalComponent) => Observable<any>,
  ): Observable<any> {
    const modalComponentRef: ComponentRef<IssueActionModalComponent> = this.modalService.open(IssueActionModalComponent);

    modalComponentRef.instance.action = actionType;
    modalComponentRef.instance.reasonIsRequired = reasonIsRequired;

    return modalComponentRef.instance.container.onClose$.pipe(
      take(1),
      filter((): boolean => modalComponentRef.instance.confirmed),
      mergeMap(() => action(modalComponentRef.instance)),
    );
  }

  private updateQAIssueStatus(newStatus: ISelectOption<AuditFindingQAStatus>, reason?: string): void {
    const formData = new FormData();
    if (!SharedCommonUtility.isNullish(reason)) {
      formData.append($falsePositiveError.reason, reason);
    }
    if (this.hasStatusEditPermissions) {
      formData.append($formFields.issueStatus, newStatus.value);
      this.subscriptions.add(
        this.manualAuditIssueService
          .updateQAIssueStatus(
            formData,
            this.workspaceId,
            this.evaluationScanId,
            this.digitalPropertyId,
            this.issue[$auditFinding.issueId],
          )
          .subscribe({
            next: (response: IManualAuditIssueServerResponse[]) => {
              this.commentSectionComponent.ngOnInit();
              this.getIssue();
              this.showTaskStatusChangedBanner = response[0].taskStatusUpdated;
            },
            error: this.onUpdateIssueError.bind(this),
          }),
      );
    } else if (this.hasDismissalPermissions) {
      if (newStatus.value === AuditFindingQAStatus.dismissed) {
        this.subscriptions.add(
          this.manualAuditIssueService
            .dismissQAIssue(
              formData,
              this.workspaceId,
              this.evaluationScanId,
              this.digitalPropertyId,
              this.issue[$auditFinding.issueId],
            )
            .subscribe({
              next: () => {
                this.commentSectionComponent.ngOnInit();
                this.getIssue();
              },
              error: this.onUpdateIssueError.bind(this),
            }),
        );
      } else if (newStatus.value === AuditFindingQAStatus.open) {
        this.subscriptions.add(
          this.manualAuditIssueService
            .restoreQAIssue(
              formData,
              this.workspaceId,
              this.evaluationScanId,
              this.digitalPropertyId,
              this.issue[$auditFinding.issueId],
            )
            .subscribe({
              next: () => {
                this.commentSectionComponent.ngOnInit();
                this.getIssue();
              },
              error: this.onUpdateIssueError.bind(this),
            }),
        );
      } else {
        throw new Error('Only open and dismissed status are allowed.');
      }
    } else {
      throw new Error('No status update permissions.');
    }
  }

  private subscribeToPermissionChecks(): void {
    this.subscriptions.add(
      this.buildEditPermissionCheck().subscribe((hasAccess: boolean) => {
        this.hasStatusEditPermissions = hasAccess;
        this.hasDeleteAttachmentPermissions = hasAccess;
        this.hasCreateAttachmentPermissions = hasAccess;
        this.changeDetector.detectChanges();
      }),
    );

    this.subscriptions.add(
      this.userAclService
        .createCheckAccessForCurrentUser()
        .pipe(
          map((adapter: AclSecurityAdapter): boolean =>
            adapter
              .useEntityLevel()
              .useWorkspaceFromUser()
              .useDigitalPropertyFromUser()
              .useFunctionalActions({ [FunctionalArea.mt_manual_evaluations]: [SecureAction.delete] })
              .check(),
          ),
        )
        .subscribe((hasAccess: boolean) => {
          this.hasDismissalPermissions = hasAccess;
          this.changeDetector.detectChanges();
        }),
    );
  }

  private buildEditPermissionCheck(): Observable<boolean> {
    return this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useEntityLevel()
          .useWorkspaceFromUser()
          .useDigitalPropertyFromUser()
          .useFunctionalActions({ [FunctionalArea.mt_manual_evaluations]: [SecureAction.update] })
          .check(),
      ),
    );
  }

  public get showDismissRestore(): boolean {
    return this.hasDismissalPermissions && !this.hasStatusEditPermissions && !this.linkedTask;
  }

  public get taskLink(): string {
    if (this.linkedTask) {
      return `/${Api.projects}/${this.linkedTask.projectId}/${Api.tasks}/${this.linkedTask._id}`;
    }
    return '';
  }

  public onLinkedCommentsTabChange(activeId: string | number): void {
    if (Number(activeId) !== LINKED_RECORD_TAB_INDEX) {
      return;
    }

    this.subscriptions.add(
      this.userService.currentTenant$.pipe(take(1)).subscribe((tenant: ITenantInfoWithPackageInfo): void => {
        this.instrumentationService.track('view linked comments', {
          source: 'finding',
          packageName: tenant?.packageName,
        });
      }),
    );
  }

  public get projectViewLink(): string {
    return `/${Api.projects}`;
  }

  public getIssue(): void {
    this.manualAuditIssueId = this.activatedRoute.snapshot.params[Api.manualAuditIssueId];
    this.evaluationScanId = this.activatedRoute.snapshot.params[Api.scan_id] ?? this.evaluationScanId;

    if (typeof this.manualAuditIssueId === 'undefined') {
      console.warn('[ViewManualAuditIssueComponent.getIssue] manual audit issue id for issue is not available', {
        evaluationScanId: this.evaluationScanId,
        manualAuditIssueId: this.manualAuditIssueId,
      });
      return;
    }

    // A response for a finding the user already navigated away from must not render under the new route.
    this.getIssueSubscription?.unsubscribe();
    this.getIssueSubscription = this.manualAuditIssueService.getIssue(this.evaluationScanId, this.manualAuditIssueId).subscribe({
      next: this.onGetIssueSuccess.bind(this),
      error: this.onGetIssueError.bind(this),
    });
    this.subscriptions.add(this.getIssueSubscription);
  }

  public getDownloadLinkForFile(file: IUploadClient): string {
    return this.restBuilder
      .create()
      .secureResources(file[$uploads._id] as string)
      .getApiUrl();
  }

  public getFormattedISODate(date: string): string {
    return DateUtility.getFormattedISODate(date);
  }

  public getFormattedFileSize(size: number): string {
    return SharedCommonUtility.formatBytes(size);
  }

  public getMaxUploadSize(): number {
    return config.files.maxUploadSize;
  }

  public getAttachedFilesTotalSize(): number {
    return Array.isArray(this.issue[$auditFinding.attachment])
      ? (this.issue[$auditFinding.attachment] as IUploadClient[])
          .map((value: IUploadClient): number => value.fileSize)
          .reduce((total: number, current: number): number => total + current, 0)
      : 0;
  }

  public openDismissModal(): void {
    this.selectedStatus = AuditFindingQAStatus.dismissed;
    this.subscriptions.add(
      this.openRuleActionModal(RuleAuditHistoryAction.dismiss, true, (modal: IssueActionModalComponent) => {
        return of(this.updateQAIssueStatus(this.dismissedStatus, modal.form.get('reason').value));
      }).subscribe(),
    );
  }

  public openRestoreModalOrUpdateQAIssueStatus(newStatus: ISelectOption<AuditFindingQAStatus>): void {
    this.selectedStatus = newStatus.value;
    if (this.issueStatus.value === AuditFindingQAStatus.dismissed) {
      this.subscriptions.add(
        this.openRuleActionModal(RuleAuditHistoryAction.restore, false, (modal: IssueActionModalComponent) => {
          return of(this.updateQAIssueStatus(newStatus, modal.form.get('reason').value));
        }).subscribe(),
      );
    } else {
      this.updateQAIssueStatus(newStatus);
    }
  }

  public triggerFileUploadDialog(): void {
    CommonUtility.triggerClickEventOnElement($auditIssueFields.files);
  }

  public onDragEnterOrOver(event: DragEvent): void {
    this.isDragAreaActive = true;

    event.preventDefault();
  }

  public onDragLeave(event: DragEvent): void {
    this.isDragAreaActive = false;

    event.preventDefault();
  }

  public onDropFiles(event: DragEvent): void {
    this.uploadFiles({ target: { files: event.dataTransfer.files } } as unknown as Event);

    this.isDragAreaActive = false;

    event.preventDefault();
  }

  public updateExistingAttachment(editAttachment: IEditAttachment): void {
    this.updateAttachment$.next(editAttachment);
  }

  public removeExistingAttachment(file: IUploadClient): void {
    this.removeAttachment$.next(file[$uploads._id] as string);
  }

  public uploadFiles(event: Event): void {
    const formData: FormData = CommonUtility.createFormData({});
    const htmlEvent: IHTMLInputEvent = event as IHTMLInputEvent;
    const files: FileList = htmlEvent.target.files;

    this.uploaderError = null;

    let counterFiles: number = 0;
    let totalFilesCount: number = Array.isArray(this.issue[$auditFinding.attachment])
      ? this.issue[$auditFinding.attachment].length
      : 0;
    let totalFilesSizes: number = this.getAttachedFilesTotalSize();

    const addFileToFormData = (file: File): void => {
      if (this.uploaderError === null) {
        totalFilesCount += 1;
        totalFilesSizes += file.size;
        if (totalFilesCount > MAX_ATTACHMENTS) {
          this.uploaderError = this.translateService.instant(
            'form-validation-attachment-quantity-exceeds-limit',
            MAX_ATTACHMENTS,
          );
        } else if (totalFilesSizes > this.getMaxUploadSize()) {
          this.uploaderError = this.translateService.instant(
            'form-validation-attachment-size-exceeds-limit',
            this.getFormattedFileSize(this.getMaxUploadSize()),
          );
        }
        formData.append($auditFinding.attachment + `_${counterFiles}`, file, file.name);
        counterFiles += 1;
      }
    };

    const excludedFiles: File[] = [];

    const isValidExtension = (file: File): boolean => {
      if (SupportedManualAuditAttachmentMimeType.includes(file.type)) {
        return true;
      }
      excludedFiles.push(file);
      return false;
    };
    Array.from(files).filter(isValidExtension).forEach(addFileToFormData);

    if (excludedFiles.length > 0) {
      this.fileUploadService.showFilesRemovedWarning(excludedFiles);
    }

    this.lastUploadFilesExcluded = excludedFiles.length > 0;

    if (this.uploaderError !== null) {
      this.notificationService.error(
        `${this.translateService.instant('upload_errors')}: ${this.uploaderError}`,
        NotificationPosition.Toast,
      );
      return;
    }

    this.uploadAttachment$.next(formData);
  }

  public pageDataLoaded(): boolean {
    return this.loadingState === pageLoadingState.loaded;
  }

  public getCustomSeverityText(severity: $severity): string {
    return this.customSeverityKeysSortedByRank?.includes(severity) ? this.customSeveritiesData.get(severity).label : null;
  }

  public ngOnInit(): void {
    this.getIssue();
    this.subscriptions.add(
      this.activatedRoute.params
        .pipe(
          map((params: Params): string => `${params[Api.scan_id]}/${params[Api.manualAuditIssueId]}`),
          distinctUntilChanged(),
          skip(1),
        )
        .subscribe((): void => {
          this.announcedRecordTabId = '';
          this.getIssue();
        }),
    );
    this.routeParams$ = this.getRouteParams();

    this.subscribeToPermissionChecks();
    this.subscribeToUpdateAttachment();

    this.subscriptions.add(
      this.userPropertyService
        .currentSelectedProperty()
        .subscribe(
          ({ digitalPropertyId, workspaceId, digitalPropertyKey, digitalPropertyName }: ICurrentSelectedProperty): void => {
            this.digitalPropertyId = digitalPropertyId;
            this.workspaceId = workspaceId;
            this.digitalPropertyKey = digitalPropertyKey;
            this.digitalPropertyName = digitalPropertyName;
            this.applyManualFindingTabTitle();
            this.updateCustomRuleDescription();
          },
        ),
    );

    this.subscriptions.add(
      this.busMessageService
        .from(BusMessageChannels.digitalPropertyChange)
        .pipe(skip(1))
        .subscribe(async (): Promise<boolean> =>
          this.issue !== null
            ? this.router.navigate(['/', Api.manual_evaluations], { replaceUrl: true })
            : Promise.resolve(false),
        ),
    );

    this.subscriptions.add(
      this.removeAttachment$
        .pipe(
          withLatestFrom(this.routeParams$, this.userPropertyService.currentSelectedProperty()),
          switchMap(([attachmentId, routeParams, selectedProperty]: [string, IRouteParams, ICurrentSelectedProperty]) =>
            this.manualAuditIssueService.deleteAttachment(
              selectedProperty.workspaceId,
              selectedProperty.digitalPropertyId,
              routeParams.evaluationScanId,
              routeParams.manualAuditIssueId,
              attachmentId,
            ),
          ),
        )
        .subscribe({
          next: this.onRemoveAttachmentSuccess.bind(this),
          error: this.onRemoveAttachmentError.bind(this),
        }),
    );

    this.subscriptions.add(
      this.uploadAttachment$
        .pipe(
          withLatestFrom(this.routeParams$, this.userPropertyService.currentSelectedProperty()),
          switchMap(([formData, routeParams, selectedProperty]: [FormData, IRouteParams, ICurrentSelectedProperty]) =>
            this.manualAuditIssueService.uploadAttachment(
              selectedProperty.workspaceId,
              selectedProperty.digitalPropertyId,
              routeParams.evaluationScanId,
              routeParams.manualAuditIssueId,
              formData,
            ),
          ),
        )
        .subscribe({
          next: this.onUploadAttachmentSuccess.bind(this),
          error: this.onUploadAttachmentError.bind(this),
        }),
    );

    this.userOrigin$ = this.userService.getUserOrigin().pipe(AngularUtility.shareRef());

    this.manualEvaluation$ = combineLatest([this.routeParams$, this.userPropertyService.currentSelectedProperty()]).pipe(
      switchMap(([routeParams, currProperty]: [IRouteParams, ICurrentSelectedProperty]) => {
        return this.manualAuditIssueService.getManualEvaluation(
          currProperty.workspaceId,
          currProperty.digitalPropertyId,
          routeParams.evaluationScanId,
        );
      }),
      AngularUtility.shareRef(),
    );

    this.evaluationOrigin$ = this.manualEvaluation$.pipe(map((evaluation: IManualEvaluation) => evaluation[$manualAudit.origin]));
    this.evaluationSource$ = this.manualEvaluation$.pipe(map((evaluation: IManualEvaluation) => evaluation[$manualAudit.source]));

    this.showIgnoredStatus$ = this.buildShowIgnoredStatus();
    this.showToReviewStatus$ = this.buildShowToReviewStatus();
    this.allRegularRestoreStatuses$ = this.buildAllRegularRestoreStatuses();

    this.subscriptions.add(
      this.tenantSeveritiesService.getAll().subscribe((customSeverities: ICustomSeveritiesMap) => {
        this.customSeveritiesData = customSeverities;
        this.customSeverityKeysSortedByRank = SharedCommonUtility.notNullishOrEmpty(this.customSeveritiesData)
          ? this.tenantSeveritiesService.getCustomSeverityKeysSortedByRank(this.customSeveritiesData)
          : [];
      }),
    );
  }
  private subscribeToUpdateAttachment(): void {
    this.subscriptions.add(
      this.updateAttachment$
        .pipe(
          withLatestFrom(this.routeParams$, this.userPropertyService.getLinkedPropertyChanges$()),
          switchMap(([editAttachment, routeParams, linkedProperty]: [IEditAttachment, IRouteParams, ILinkedPropertyData]) =>
            this.manualAuditIssueService.updateAttachment(
              linkedProperty[Api.workspaceId],
              linkedProperty[Api.digitalPropertyId],
              routeParams.evaluationScanId,
              routeParams.manualAuditIssueId,
              editAttachment.uploadId,
              this.getUpdateAttachmentFormData(editAttachment),
            ),
          ),
        )
        .subscribe({
          next: this.onUpdateAttachmentSuccess.bind(this),
          error: this.onUpdateAttachmentError.bind(this),
        }),
    );
  }

  private getUpdateAttachmentFormData(editAttachment: IEditAttachment): FormData {
    const formData: FormData = new FormData();
    formData.set($uploads.altText, editAttachment.altText);

    return formData;
  }

  private onUpdateAttachmentSuccess(upload: IUploadClient): void {
    this.notificationService.success(
      this.translateService.instant('manual_audit_issue_attachment_update_success'),
      NotificationPosition.Toast,
    );

    const attachments: IUploadWithThumbnailClient[] = [...this.issue[$auditFinding.attachment]];
    const indexToReplace: number = attachments.findIndex(
      (attachment: IUploadWithThumbnailClient) => upload[$uploads._id] === attachment[$uploads._id],
    );
    attachments[indexToReplace] = upload;

    this.issue[$auditFinding.attachment] = attachments;
    this.changeDetector.detectChanges();
  }

  private onUpdateAttachmentError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError(
      'manual_audit_issue_attachment_update_failed',
      response,
      ViewManualAuditIssueComponent.name,
    );
  }

  private doDeleteIssue(): void {
    this.subscriptions.add(
      this.manualAuditIssueService.deleteIssue(this.evaluationScanId, this.manualAuditIssueId).subscribe({
        next: () => {
          this.router
            .navigate(['/', Api.manual_evaluations, this.evaluationScanId, Api.results], {
              queryParams: { [ApiQueryOption.showSuccessAlert]: true },
              queryParamsHandling: 'merge',
            })
            .then();
        },
        error: this.loadErrorHandler.handleErrorBindable.bind(
          this.loadErrorHandler,
          'error_deleting_finding',
          ViewManualAuditIssueComponent.name,
        ),
      }),
    );
  }

  private changeFlagComment(
    flagReason: ManualFindingFlagReason | undefined,
    message: string | undefined,
    addFlag: boolean,
  ): void {
    const angularFormData: FormData = CommonUtility.createFormData({
      [$manualAuditIssueComment.manualAuditFindingId]: this.issue._id,
      ...(message ? { [$manualAuditIssueComment.message]: message } : {}),
      ...(flagReason ? { [$manualAuditIssueComment.flagReason]: flagReason } : {}),
    });
    this.subscriptions.add(
      this.commentService
        .changeFlagComment(
          angularFormData,
          this.workspaceId,
          this.digitalPropertyId,
          this.evaluationScanId,
          this.manualAuditIssueId,
          addFlag,
        )
        .subscribe((comment: IComment): void => {
          this.getIssue();
          this.commentSectionComponent.ngOnInit();
        }),
    );
    return;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public isIssueStatusSelected(status: AuditFindingQAStatus): boolean {
    return this.selectedStatus === status;
  }

  public get showStepsToReproduce(): boolean {
    return SharedCommonUtility.notNullishOrEmpty(this.issue[$auditFinding.stepsToReproduce]);
  }

  public get showUserImpact(): boolean {
    return SharedCommonUtility.notNullishOrEmpty(this.issue[$auditFinding.userImpact]);
  }

  public copyXpathToClipboard(): void {
    this.clipboardUtilityService.copyTextToClipboard(this.issue[$auditFinding.elementReference]);
  }

  public copyElementToClipboard(): void {
    this.clipboardUtilityService.copyTextToClipboard(this.issue[$auditFinding.element]);
  }

  public deleteIssue(): void {
    let translation_key: string = 'cannot_be_undone';
    if (SharedCommonUtility.notNullish(this.issue.task)) {
      translation_key = 'will_detach_and_cannot_be_undone';
    }
    const modalRef: NgbModalRef | NgbOffcanvasRef = this.dsModalService.open(DeleteConfirmDialogComponent, DsModalVariant.center);
    modalRef.componentInstance.translation_key = translation_key;
    this.subscriptions.add(
      (modalRef.componentInstance as DeleteConfirmDialogComponent).confirmed.subscribe(() => {
        this.doDeleteIssue();
      }),
    );
  }

  public openChangeFlagModal(addFlag: boolean): void {
    const changeFlagTo: boolean = !addFlag;
    const modalRef: ChangeFindingFlagModalComponent = this.dsModalService.open(
      ChangeFindingFlagModalComponent,
      DsModalVariant.center,
    ).componentInstance;
    modalRef.addFlag.set(changeFlagTo);
    this.subscriptions.add(
      modalRef.confirmed.subscribe(({ flagReason, message }: IFlagCommentFormValue) => {
        this.changeFlagComment(flagReason, message, changeFlagTo);
      }),
    );
  }
}
